import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Chip,
} from '@mui/material';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import { printReport } from '../../utils/pdfPrint';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { ReportPage, ExportMenu } from './ReportKit';

const STATUS_OPTIONS = [
  { value: '', label: 'Të gjitha' },
  { value: 'pending', label: 'Në pritje' },
  { value: 'in_progress', label: 'Në proces' },
  { value: 'completed', label: 'Të përfunduara' },
  { value: 'cancelled', label: 'Anuluara' },
];

const STATUS_LABELS = {
  pending: 'Në pritje',
  in_progress: 'Në proces',
  completed: 'Të përfunduara',
  cancelled: 'Anuluara',
};

const PRIORITY_OPTIONS = [
  { value: '', label: 'Të gjitha' },
  { value: 'low', label: 'E ulët' },
  { value: 'medium', label: 'Mesatare' },
  { value: 'high', label: 'E lartë' },
  { value: 'urgent', label: 'Urgjente' },
];

const PRIORITY_LABELS = {
  low: 'E ulët',
  medium: 'Mesatare',
  high: 'E lartë',
  urgent: 'Urgjente',
};

const DATE_FIELDS = [
  { value: 'created_at', label: 'Krijuar' },
  { value: 'due_date', label: 'Afati' },
  { value: 'planned_start', label: 'Planifikuar (fillimi)' },
];

const statusColors = {
  pending: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancelled: 'error',
};

const priorityColors = {
  low: 'default',
  medium: 'primary',
  high: 'warning',
  urgent: 'error',
};

const toDateKey = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatMinutes = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === '') return '-';
  const total = Number(minutes);
  if (!Number.isFinite(total)) return '-';
  const rounded = Math.max(0, Math.round(total));
  const hrs = Math.floor(rounded / 60);
  const mins = rounded % 60;
  if (!hrs && !mins) return '0m';
  return `${hrs ? `${hrs}h ` : ''}${mins}m`.trim();
};

export default function TaskManagementReport() {
  const { t } = useTranslation();

  // STATUS_OPTIONS / STATUS_LABELS / PRIORITY_OPTIONS / PRIORITY_LABELS / DATE_FIELDS
  // are the module-level constants defined above (already in Albanian).
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [dateField, setDateField] = useState('created_at');
  const [emailOpen, setEmailOpen] = useState(false);
  const [workers, setWorkers] = useState([]);

  const pdfCols = useMemo(() => [
    { label: t('report.title'), key: 'title', align: 'left', width: '30%' },
    { label: t('report.status'), key: r => STATUS_LABELS[r.status] || r.status || '—', align: 'left', width: '15%' },
    { label: t('report.priority'), key: r => PRIORITY_LABELS[r.priority] || r.priority || '—', align: 'left', width: '15%' },
    { label: t('report.worker'), key: r => getUserLabel(r), align: 'left', width: '20%' },
    { label: t('report.department'), key: r => getDepartmentLabel(r), align: 'left', width: '20%' },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, STATUS_LABELS, PRIORITY_LABELS]);

  useEffect(() => {
    loadFilters();
    loadTasks();
  }, []);

  const loadFilters = async () => {
    try {
      const [usersRes, workersRes, deptRes] = await Promise.all([
        apiService().get('cms_users').catch(() => ({ data: [] })),
        apiService().get('workers').catch(() => ({ data: [] })),
        apiService().get('departments'),
      ]);
      // Prefer cms_users if available, fallback to workers
      const cmsUsers = usersRes?.data || [];
      const workersData = workersRes?.data || [];
      setWorkers(workersData);
      const workers = workersData;
      if (cmsUsers.length > 0) {
        setUsers(cmsUsers);
      } else {
        // Normalize workers to match expected user shape {id, name, surname}
        setUsers(workers.map((w) => ({
          id: w.Id || w.id,
          name: w.FirstName || w.name || '',
          surname: w.LastName || w.surname || '',
        })));
      }
      setDepartments((deptRes?.data || []).map(d => ({
        id: d.Id ?? d.id,
        name: d.Name || d.name || '',
      })));
    } catch (_) {
      setUsers([]);
      setDepartments([]);
    }
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const res = await apiService().get('tasks');
      setTasks(res?.data || []);
    } catch (_) {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const userMap = useMemo(() => {
    const map = new Map();
    users.forEach((u) => map.set(String(u.id), u));
    return map;
  }, [users]);

  const departmentMap = useMemo(() => {
    const map = new Map();
    departments.forEach((d) => { const k = String(d.id ?? ''); if (k) map.set(k, d); });
    return map;
  }, [departments]);

  // Normalize task fields: support both camelCase and snake_case and PascalCase field names
  const normalizeTask = (task) => ({
    ...task,
    id: task.id || task.Id || task.ID,
    title: task.title || task.Title || task.Name || task.name || '',
    status: (task.status || task.Status || '').toLowerCase().replace(/ /g, '_'),
    priority: (task.priority || task.Priority || '').toLowerCase().replace(/ /g, '_'),
    assigned_to: task.assigned_to || task.AssignedTo || task.assignedTo || task.UserId,
    department_id: task.department_id || task.DepartmentId || task.departmentId || task.Department,
    created_at: task.created_at || task.CreatedAt || task.createdat,
    due_date: task.due_date || task.DueDate || task.duedate,
    planned_start: task.planned_start || task.PlannedStart,
    planned_minutes: task.planned_minutes || task.PlannedMinutes || task.PlannedTime,
    actual_minutes: task.actual_minutes || task.ActualMinutes || task.ActualTime,
  });

  const normalizedTasks = useMemo(() => tasks.map(normalizeTask), [tasks]);

  const filteredTasks = useMemo(() => {
    return normalizedTasks.filter((task) => {
      if (status && task.status !== status) return false;
      if (priority && task.priority !== priority) return false;
      if (assignedTo && String(task.assigned_to) !== String(assignedTo)) return false;
      if (departmentId && String(task.department_id) !== String(departmentId)) return false;
      if (from || to) {
        const dateKey = toDateKey(task?.[dateField]);
        if (!dateKey) return false;
        if (from && dateKey < from) return false;
        if (to && dateKey > to) return false;
      }
      return true;
    });
  }, [normalizedTasks, status, priority, assignedTo, departmentId, from, to, dateField]);

  const summary = useMemo(() => {
    const totals = {
      count: filteredTasks.length,
      planned: 0,
      actual: 0,
      statusCounts: {
        pending: 0,
        in_progress: 0,
        completed: 0,
        cancelled: 0,
      },
    };
    filteredTasks.forEach((t) => {
      totals.planned += Number(t.planned_minutes || 0);
      totals.actual += Number(t.actual_minutes || 0);
      const statusKey = String(t.status || '').toLowerCase();
      if (totals.statusCounts[statusKey] !== undefined) {
        totals.statusCounts[statusKey] += 1;
      }
    });
    return totals;
  }, [filteredTasks]);

  const getUserLabel = (task) => {
    if (task?.assigned_to_name) return task.assigned_to_name;
    const user = userMap.get(String(task?.assigned_to || ''));
    if (!user) return '-';
    return `${user.name || ''} ${user.surname || ''}`.trim() || '-';
  };

  const getDepartmentLabel = (task) => {
    if (task?.department_name) return task.department_name;
    const dept = departmentMap.get(String(task?.department_id || ''));
    return dept?.name || '-';
  };

  const dateLabel = useMemo(() => {
    const entry = DATE_FIELDS.find((f) => f.value === dateField);
    return entry ? entry.label : 'Data';
  }, [dateField]);

  const exportPdf = () => printReport({
    title: t('reports.tasks'),
    subtitle: `${filteredTasks.length} ${t('report.tasks')}`,
    orientation: 'landscape',
    columns: pdfCols,
    rows: filteredTasks,
    badge: { label: `${summary.statusCounts.completed} ${t('report.taskCompleted')}` },
  });

  const handleExport = () => {
    const headers = [
      'ID',
      t('report.title'),
      t('report.status'),
      t('report.priority'),
      t('report.worker'),
      t('report.department'),
      t('report.date'),
      t('report.plannedMin'),
      t('report.actualMin'),
    ];
    const lines = [headers.join(',')];
    filteredTasks.forEach((task) => {
      const values = [
        task.id || '',
        task.title || '',
        STATUS_LABELS[task.status] || task.status || '',
        PRIORITY_LABELS[task.priority] || task.priority || '',
        getUserLabel(task),
        getDepartmentLabel(task),
        toDateKey(task?.[dateField]) || '',
        task.planned_minutes || 0,
        task.actual_minutes || 0,
      ];
      lines.push(values.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    });
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'task_management_report.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <ReportPage>
      <PageHeader title={t('reports.tasks')} />
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            {t('report.filters')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label={t('report.date')}
              value={dateField}
              onChange={(e) => setDateField(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              {DATE_FIELDS.map((f) => (
                <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="date"
              label={t('report.from')}
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              label={t('report.to')}
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
            <TextField
              select
              size="small"
              label={t('report.status')}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('report.priority')}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              {PRIORITY_OPTIONS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('report.worker')}
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">{t('report.all')}</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {(u.name || '').trim()} {(u.surname || '').trim()}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('report.department')}
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">{t('report.all')}</MenuItem>
              {departments.map((d) => (
                <MenuItem key={d.id ?? d.Id ?? String(Math.random())} value={d.id ?? d.Id}>{d.name || d.Name}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={loadTasks} disabled={loading}>
                {loading ? t('report.loading') : t('report.load')}
              </Button>
              <ExportMenu onCsv={handleExport} onPdf={exportPdf} onEmail={() => setEmailOpen(true)} />
            </Stack>
          </Box>

          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="body2">{t('report.total')}: {summary.count}</Typography>
            <Typography variant="body2">{t('report.planned')}: {formatMinutes(summary.planned)}</Typography>
            <Typography variant="body2">{t('report.actual')}: {formatMinutes(summary.actual)}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Chip size="small" color="warning" label={`${t('report.taskPending')}: ${summary.statusCounts.pending}`} />
            <Chip size="small" color="info" label={`${t('report.taskInProgress')}: ${summary.statusCounts.in_progress}`} />
            <Chip size="small" color="success" label={`${t('report.taskCompleted')}: ${summary.statusCounts.completed}`} />
            <Chip size="small" color="error" label={`${t('report.taskCancelled')}: ${summary.statusCounts.cancelled}`} />
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('report.taskLabel')}</TableCell>
                  <TableCell>{t('report.status')}</TableCell>
                  <TableCell>{t('report.priority')}</TableCell>
                  <TableCell>{t('report.worker')}</TableCell>
                  <TableCell>{t('report.department')}</TableCell>
                  <TableCell>{dateLabel}</TableCell>
                  <TableCell>{t('report.planned')}</TableCell>
                  <TableCell>{t('report.actual')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTasks.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell>{task.title || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={STATUS_LABELS[task.status] || task.status || '-'}
                        color={statusColors[task.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={PRIORITY_LABELS[task.priority] || task.priority || '-'}
                        color={priorityColors[task.priority] || 'default'}
                      />
                    </TableCell>
                    <TableCell>{getUserLabel(task)}</TableCell>
                    <TableCell>{getDepartmentLabel(task)}</TableCell>
                    <TableCell>{toDateKey(task?.[dateField]) || '-'}</TableCell>
                    <TableCell>{formatMinutes(task.planned_minutes)}</TableCell>
                    <TableCell>{formatMinutes(task.actual_minutes)}</TableCell>
                  </TableRow>
                ))}
                {filteredTasks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary">{t('report.noData')}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      <SendReportEmailDialog
        open={emailOpen} onClose={() => setEmailOpen(false)}
        reportTitle={t('reports.tasks')}
        subtitle={`${filteredTasks.length} ${t('report.tasks')}`}
        columns={pdfCols} rows={filteredTasks}
        workers={workers}
        orientation="landscape"
      />
    </ReportPage>
  );
}
